/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css  } from "@emotion/core";
import * as React from "react";
import { Fragment } from "react";
import {
	Button,
	CaretDownIcon,
	Container,
	ExchangeIcon,
	PencilIcon,
	PositionedOverlay,
	Select,
	TextInput,
	Theme
} from "@ice/components";
import ColorChooserSet from "./ColorChooserSet";
import ColorSetEditor from "./ColorSetEditor";
import {ColorUtils, getColorPalettesForScheme, PaletteScheme} from "../../util/ColorUtils";


export const MAX_CUSTOM_COLORS = 10;



interface ColorPaletteChooserProps {
	colors: string[];
	isGradient?: boolean;
	customPaletteIndex?: number;
	onPaletteSelected?: (colors: string[], isConfirmed: boolean) => void;
	onCustomPaletteSelected?: (index: number) => void;
	onCustomPaletteSaved?: (index: number, colors: string[]) => void;
}


interface ColorPaletteChooserState {
	colors: string[];
	showDetails: boolean;
	scheme: PaletteScheme;
	colorCount: number;
	reverse: boolean;
	isEditing: boolean;
	forcedUpdateCounter: number;
}


class ColorPaletteChooser extends React.Component<ColorPaletteChooserProps, ColorPaletteChooserState> {

	private selectedPaletteDivRef = React.createRef<HTMLDivElement>();

	private shouldConfirmOnClose = false;

	constructor(props: Readonly<ColorPaletteChooserProps> | ColorPaletteChooserProps) {
		super(props);
		this.state = {
			colors: this.props.colors,
			showDetails: false,
			scheme: PaletteScheme.diverging,
			colorCount: this.props.colors?.length ?? 5,
			reverse: false,
			isEditing: false,
			forcedUpdateCounter: 0
		};
	}

	private showDetails = (b: boolean, editing: boolean = false) => {
		this.setState({showDetails: b, isEditing: editing});
	}

	private setScheme = (scheme: PaletteScheme) => {
		this.setState({scheme: scheme});
	}

	private setColorCount = (event) => {
		const numStr = event.target.valueAsNumber;
		const num = Number(numStr);
		if (num && num <= 10) {
			this.setState({colorCount: num});
		}
	}

	private applyPalette = (colors: string[], isConfirmed: boolean) => {
		this.props.onPaletteSelected(colors, isConfirmed);
	};

	private deleteCustomPalette = (index: number) => {
		ColorUtils.deleteCustomPalette(index);
		this.setState({forcedUpdateCounter: this.state.forcedUpdateCounter + 1});
	}

	private renderPaletteSelection = (): JSX.Element => {
		return (
			<Fragment key={`k_${this.state.forcedUpdateCounter}`}>
				<div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
					<Select<PaletteScheme>
						includeSelectionsInOptions={true}
						clearable={false}
						searchable={false}
						minimal={true}
						getOptionLabel={(i) => Object.values(PaletteScheme)[i]}
						getSelectionLabel={(f) => f}
						getValue={(i) => Object.values(PaletteScheme)[i]}
						optionCount={Object.values(PaletteScheme).length}
						value={this.state.scheme}
						onChange={(scheme) => this.setScheme(scheme as PaletteScheme)}
					/>
					{
						this.state.scheme !== PaletteScheme.custom &&
						<Fragment>
							<TextInput style={{ width: "5em", marginLeft: "0.5em" }} onChange={this.setColorCount} type="number" minimal={true} value={String(this.state.colorCount)} />
							<span style={{ marginLeft: "0.5em" }}>colors</span>
						</Fragment>
					}
					<div style={{ flexGrow: 1 }}></div>
					<Button minimal={true} title={"Reverse colors"}>
						<ExchangeIcon onClick={() => this.setState({ reverse: !this.state.reverse })} />
					</Button>
				</div>
				<div>{this.renderPalettesInDetails()}</div>
				<div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
					<Button
						intent={"primary"}
						onClick={() => {
							this.applyPalette(this.state.colors, true);
							this.showDetails(false);
						}}
					>
						OK
					</Button>
					<div style={{ width: "0.4rem" }} />
					<Button
						onClick={() => {
							this.shouldConfirmOnClose = false;
							this.setState({ colors: this.props.colors, showDetails: false });
							this.applyPalette(this.props.colors, true);
						}}
					>
						Cancel
					</Button>
				</div>
			</Fragment>
		);
	}

	private renderPaletteEdit = (): JSX.Element => {
		return <ColorSetEditor
			prevColors={this.props.colors}
			colors={this.state.colors}
			customPaletteIndex={ColorUtils.isTheSameAsCustomPalette(this.props.customPaletteIndex, this.state.colors) ? this.props.customPaletteIndex : -1}
			onColorsChange={colors => {
				this.setState({colors: colors});
				this.shouldConfirmOnClose = true;
				this.applyPalette(colors, false);
			}}
			onCancel={() => {
				this.shouldConfirmOnClose = false;
				this.showDetails(false);
			}}
			onConfirm={() => {
				this.applyPalette(this.state.colors, true);
				this.showDetails(false);
			}}
			savePalette={(index) => this.props.onCustomPaletteSaved && this.props.onCustomPaletteSaved(index, this.state.colors)}
		/>;
	}

	private renderPalettesInDetails = (): JSX.Element => {
		const palettes = getColorPalettesForScheme(this.state.scheme, this.state.colorCount);
		const content = [];
		let hasSomePalettes = false;
		if (palettes && palettes.length > 0) {
			for (const p of palettes) {
				if (p) {
					hasSomePalettes = true;
					break;
				}
			}
		}
		if (hasSomePalettes) {
			for (let i = 0; i < palettes.length; i++) {
				content.push(
					<ColorChooserSet
						colors={palettes[i]}
						isHighlightable={true}
						prefixElement={<span style={{width: "2rem", paddingLeft: "0.4rem", fontSize: "0.8em", fontWeight: "bold", cursor: "pointer"}}>{i + 1}:</span>}
						reverse={this.state.reverse}
						onSelect={colors => {
							this.setState({colors: colors});
							this.shouldConfirmOnClose = true;
							this.applyPalette(colors, false);
							if (this.state.scheme === PaletteScheme.custom && this.props.onCustomPaletteSelected) {
								this.props.onCustomPaletteSelected(i)
							}
						}}
						onDelete={this.state.scheme === PaletteScheme.custom ? () => this.deleteCustomPalette(i) : null}
					/>
				)
			}
		} else {
			content.push(<div style={{padding: "1em 0.4rem"}}>No palettes defined!</div>);
		}

		return (
			<div key={`sch_${this.state.scheme}_${this.state.colorCount}`}>
				{content}
			</div>
		)
	}

	private renderGradient = (colors: string[]): JSX.Element => {
		return (
			<span style={{
				display: "block",
				width: `18rem`,
				height: `1.6rem`,
				backgroundImage: `linear-gradient(to right,${colors.reduce((prev, c) => prev + "," + c)})`,
				margin: "0.36em 0.2em"
			}}/>
		)
	}

	render() {
		return (
			<div style={{ display: "flex", flexDirection: "column" }}>
				<div
					ref={this.selectedPaletteDivRef}
					css={(theme: Theme) => css`
						display: flex;
						flex-direction: row;
						align-items: center;
						border: 1px solid ${theme.borderColor.normal};
						border-radius: 0.4rem;
						padding-left: 0.4rem;
						cursor: pointer;
					`}
					onClick={() => {
						if (this.state.isEditing) {
							this.showDetails(true, false);
						} else {
							this.showDetails(!this.state.showDetails, false);
						}
					}}
				>
					{
						this.props.isGradient ?
							this.renderGradient(this.state.colors) :
							<ColorChooserSet key={`p_${this.state.colors}`} colors={this.state.colors} />
					}

					<div style={{ flexGrow: 1 }} />
					<PencilIcon
						width="fixed"
						title={"Edit colors"}
						onClick={(event) => {
							this.showDetails(true, true);
							event.stopPropagation();
						}}
					/>
					<CaretDownIcon width="fixed" title={"Choose some of the predefined color schemes"} />
				</div>
				{this.state.showDetails && (
					<PositionedOverlay
						closeOnClickOutside
						closeOnEscapeKeyPress
						isInside={(t) => this.selectedPaletteDivRef?.current?.contains(t)}
						placement={"bottom-start"}
						target={this.selectedPaletteDivRef.current}
						onClose={() => {
							if (this.shouldConfirmOnClose) {
								this.applyPalette(this.state.colors, true);
							}
							this.showDetails(false);
						}}
					>
						{(getProps) => (
							<Container {...getProps()}>
								<div
									css={(theme: Theme) => css`
										background-color: ${theme.backgroundColor.default};
									`}
									style={{
										border: "1px solid rgba(128, 128, 128, 0.4)",
										borderRadius: "0.2rem",
										padding: "0.4em",
										display: "flex",
										flexDirection: "column",
										minWidth: "25rem"
									}}
								>
									{this.state.isEditing ? this.renderPaletteEdit() : this.renderPaletteSelection()}
								</div>
							</Container>
						)}
					</PositionedOverlay>
				)}
			</div>
		);
	}
}

export default ColorPaletteChooser;
